import React, { useCallback, useEffect, useState } from "react";
import { RotateCcw } from "react-feather";
import { useApi } from "../hooks/useApi";

// Organizations deleted in the last 30 days, with Restore. The parent passes a new reloadKey after an
// approval deletes an organization, and reloads its own list through onRestored.
const RecentlyDeletedOrganizations = ({ reloadKey, onRestored }) => {
  const apiCall = useApi();
  const [organizations, setOrganizations] = useState([]);
  const [error, setError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiCall("GET", "/SuperAdmin/organizations/deleted");
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load recently deleted organizations");
    }
  }, [apiCall]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleRestore = async (org) => {
    setRestoringId(org.organizationId);
    setError(null);
    try {
      await apiCall("POST", `/SuperAdmin/organizations/${org.organizationId}/restore`, null);
      await load();
      if (onRestored) await onRestored();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to restore organization");
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold text-content-primary mb-1">Recently deleted</h2>
      <p className="text-sm text-content-secondary mb-4">
        Organizations deleted in the last 30 days. Restoring one brings it back with its users and data.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
          {error}
        </div>
      )}

      <div className="bg-theme-secondary border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-theme-primary/50">
              {["Organization", "Org ID", "Deleted", "Requested by", "Approved by", "Actions"].map((h) => (
                <th
                  key={h}
                  className="text-left px-6 py-3 text-sm font-medium text-content-secondary"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <tr key={org.organizationId} className="border-b border-border last:border-b-0">
                <td className="px-6 py-3 text-sm text-content-primary font-medium">
                  {org.organizationName}
                </td>
                <td className="px-6 py-3 text-sm text-content-secondary">{org.organizationId}</td>
                <td className="px-6 py-3 text-sm text-content-secondary">
                  {org.deletedAt ? new Date(org.deletedAt).toLocaleString() : ""}
                </td>
                <td className="px-6 py-3 text-sm text-content-secondary">
                  {org.deletionRequestedByName || "—"}
                </td>
                <td className="px-6 py-3 text-sm text-content-secondary">
                  {org.deletionApprovedByName || "—"}
                </td>
                <td className="px-6 py-3">
                  <button
                    onClick={() => handleRestore(org)}
                    disabled={restoringId === org.organizationId}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-accent-cyan border border-accent-cyan/40 rounded-lg hover:bg-accent-cyan/10 transition-all disabled:opacity-50"
                    aria-label={`Restore ${org.organizationName}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {restoringId === org.organizationId ? "Restoring..." : "Restore"}
                  </button>
                </td>
              </tr>
            ))}
            {organizations.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-center text-sm text-content-muted">
                  No organizations deleted in the last 30 days
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentlyDeletedOrganizations;
